import React from "react";
import moment from "moment";
import { Page, View, Text } from "@react-pdf/renderer";

import getPDFTranslations from "utils/translation/f280-pdf";
import { APP_VERSION } from "utils/constants/version";
import convertUnit from "utils/convertUnit";

import { styles, COLORS } from "../f280Pdf/pdfStyles";
import { Row, NumField, SmallTitle, Field } from "../f280Pdf/pdfPrimitives";
import { TitleBar, Footer, DisclaimerProjectRow } from "../f280Pdf/pdfShared";

const MAX_ROWS = 39;

// Full-height vertical divider (matches the paired-cell rule on the other pages).
const VDivider = () => <View style={{ width: 1, backgroundColor: COLORS.black }} />;

const SplitCell = ({ children, style }) => (
    <View style={[styles.fullFlex, { flexDirection: "row" }, style]}>{children}</View>
);

// Page 3 (optional) of the F280 report — per-room heating/cooling results.
// Vector react-pdf rebuild; data shaping and unit conversion are unchanged.
const RoomByRoomPDFPage = ({
    language,
    issuedForTheUseOf,
    projectNo,
    name,
    site,
    lot,
    address,
    cityProvince,
    postalCode,
    storeyCategories,
    roomByRoomResults,
    displayUnits,
    heatLossValue,
    latHeatGainValue,
    latentMultiplier,
}) => {
    const storeysPresent = storeyCategories.filter(({ id }) => id !== "other");

    const roomByRoomRows = storeysPresent
        .map((storeyCat) => {
            const roomsInStorey = roomByRoomResults[storeyCat.id] || {};

            const sortedRoomKeys = Object.keys(roomsInStorey)
                .filter((roomKey) => !roomKey.includes("-default-"))
                .sort((keyA, keyB) => (roomsInStorey[keyA]?.name || "").localeCompare(roomsInStorey[keyB]?.name || ""));

            return sortedRoomKeys.map((roomKey) => ({
                name: roomsInStorey[roomKey]?.name || "N/A",
                heatLoss: parseFloat(
                    convertUnit({
                        value: roomsInStorey[roomKey].heatLoss,
                        type: "power",
                        inputUnit: "W",
                        outputUnit: displayUnits === "Metric" ? "W" : "BTU/h",
                    })
                ).toFixed(2),
                heatGain: parseFloat(
                    convertUnit({
                        value: latentMultiplier * roomsInStorey[roomKey].heatGain,
                        type: "power",
                        inputUnit: "W",
                        outputUnit: displayUnits === "Metric" ? "W" : "BTU/h",
                    })
                ).toFixed(2),
            }));
        })
        .flat()
        .filter((_, index) => index < MAX_ROWS);

    const filledRows = new Array(MAX_ROWS)
        .fill({})
        .map((_, index) =>
            roomByRoomRows[index]
                ? roomByRoomRows[index]
                : { name: "no_value", heatLoss: "no_value", heatGain: "no_value" }
        );

    const { t } = getPDFTranslations(language);
    const unitLabel = displayUnits === "Metric" ? "W" : "BTU/h";

    return (
        <Page size="LETTER" style={styles.page}>
            {/* wrap={false}: keep the whole results form on one page. A wrapped
                header/location cell that tips the total over the page height would
                otherwise push just the footer onto a near-empty extra page. */}
            <View style={styles.body} wrap={false}>
                <TitleBar
                    title={t("f280pdf.roomByRoomCalculationResults", "ROOM by ROOM CALCULATION RESULTS !fallback")}
                    rightPrimary={t("f280pdf.standardForm.1", "CSA-F280-M12 [!!]")}
                    rightSecondary={t("f280pdf.standardForm.2", "Standard Form No. 1 [!!]")}
                />

                {/* Disclaimer + project number */}
                <DisclaimerProjectRow t={t} issuedForTheUseOf={issuedForTheUseOf} projectNo={projectNo} />

                {/* Building location */}
                <SmallTitle>{t("f280pdf.buildingLoc", "BUILDING LOCATION [!!]")}</SmallTitle>
                <Row>
                    <Field
                        label={t("f280pdf.model", "Model: [!!]")}
                        value={name}
                        num="3"
                        containerStyle={styles.borderRight}
                    />
                    <SplitCell>
                        <Field
                            label={t("f280pdf.site", "Site: [!!]")}
                            value={site}
                            num="6"
                            containerStyle={styles.borderRight}
                        />
                        <Field label={t("f280pdf.lot", "Lot: [!!]")} value={lot} num="7" />
                    </SplitCell>
                </Row>
                <Row>
                    <Field
                        label={t("f280pdf.address", "Address: [!!]")}
                        value={address}
                        num="4"
                        containerStyle={styles.borderRight}
                    />
                    <SplitCell>
                        <Field
                            label={`${t("f280pdf.city", "City [!!]")}/ ${t("f280pdf.province", "Prov: [!!]")}`}
                            value={cityProvince}
                            num="5"
                            labelStyle={{ width: 40 }}
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={`${t("f280pdf.postalCode.1", "Post. [!!]")} ${t("f280pdf.postalCode.2", "code: [!!]")}`}
                            value={postalCode}
                            num="8"
                        />
                    </SplitCell>
                </Row>

                {/* Results header */}
                <View style={styles.smallTitle}>
                    <Text style={styles.smallTitleH2}>
                        {t("f280pdf.calculationResultsRoomByRoom", "CALCULATION RESULTS - ROOM by ROOM !fallback")}
                    </Text>
                    <NumField style={{ right: "auto", left: 16 }}>70</NumField>
                </View>

                {/* Table column headers */}
                <Row>
                    <View style={styles.rxrIndexCell}>
                        <Text>#</Text>
                    </View>
                    <View
                        style={[
                            styles.fullFlex,
                            styles.borderRight,
                            { justifyContent: "center", alignItems: "center", position: "relative", minHeight: 16 },
                        ]}
                    >
                        <Text style={styles.rxrHeaderText}>{t("f280pdf.roomName", "Room, Name !fallback")}</Text>
                        <NumField>71</NumField>
                    </View>
                    <SplitCell>
                        <View
                            style={[
                                styles.fullFlex,
                                styles.borderRight,
                                { justifyContent: "center", alignItems: "center", position: "relative", minHeight: 16 },
                            ]}
                        >
                            <Text style={styles.rxrHeaderText}>
                                {t("f280pdf.heatingBtuH", "Heating (Btu/h) !fallback")} ({unitLabel})
                            </Text>
                            <NumField>72</NumField>
                        </View>
                        <View
                            style={[
                                styles.fullFlex,
                                { justifyContent: "center", alignItems: "center", position: "relative", minHeight: 16 },
                            ]}
                        >
                            <Text style={styles.rxrHeaderText}>
                                {t("f280pdf.coolingBtuH", "Cooling (Btu/h) !fallback")} ({unitLabel})
                            </Text>
                            <NumField>73</NumField>
                        </View>
                    </SplitCell>
                </Row>

                {/* Data rows */}
                {filledRows.map((room, index) => {
                    const filled = room.name !== "no_value";
                    return (
                        <View style={styles.rxrDataRow} key={index}>
                            <View style={styles.rxrIndexCell}>
                                <Text>{index + 1}</Text>
                            </View>
                            <View style={styles.rxrNameCell}>
                                <Text style={styles.rxrValueText}>{filled ? room.name : ""}</Text>
                            </View>
                            <SplitCell>
                                <View style={[styles.rxrValueCell, styles.borderRight]}>
                                    <Text style={styles.rxrValueText}>{filled ? `${room.heatLoss}` : ""}</Text>
                                </View>
                                <View style={styles.rxrValueCell}>
                                    <Text style={styles.rxrValueText}>{filled ? `${room.heatGain}` : ""}</Text>
                                </View>
                            </SplitCell>
                        </View>
                    );
                })}

                {/* Ventilation loss / latent gain */}
                <Row>
                    <View style={{ width: 26 }} />
                    <View style={[styles.fullFlex, styles.borderRight, { justifyContent: "center", minHeight: 16 }]}>
                        <Text style={{ textAlign: "right", fontSize: 6 }}>
                            <Text style={{ fontFamily: "Helvetica-Bold" }}>
                                {t("f280pdf.ventilationLatent.1", "Ventilation Loss")}
                            </Text>{" "}
                            <Text style={styles.smallLabel}>
                                {t("f280pdf.ventilationLatent.2", "(if separate)")}
                                {/* {t("f280pdf.ventilationLatent.3", "74")} — field number hidden for now */}
                            </Text>{" "}
                            <Text style={{ fontFamily: "Helvetica-Bold" }}>
                                {t("f280pdf.ventilationLatent.4", "& Latent Gain")}
                            </Text>{" "}
                            <Text style={styles.smallLabel}>
                                {t("f280pdf.ventilationLatent.5", "(if separate, value or multiplier)")}
                                {/* {t("f280pdf.ventilationLatent.6", "75")} — field number hidden for now */}
                            </Text>
                        </Text>
                    </View>
                    <SplitCell>
                        <View style={[styles.rxrValueCell, styles.borderRight, { alignItems: "flex-start" }]}>
                            <Text style={styles.rxrValueText}>Included</Text>
                        </View>
                        <View style={[styles.rxrValueCell, { alignItems: "flex-start" }]}>
                            <Text style={styles.rxrValueText}>Included (x1.3)</Text>
                        </View>
                    </SplitCell>
                </Row>

                {/* Totals */}
                <Row>
                    <View style={{ width: 26 }} />
                    <View style={[styles.fullFlex, styles.borderRight, { justifyContent: "center", minHeight: 16 }]}>
                        <Text style={{ textAlign: "right", fontSize: 6 }}>
                            <Text style={{ fontFamily: "Helvetica-Bold" }}>Total Building Loss</Text>{" "}
                            <Text style={styles.smallLabel}>(5.2.7)</Text> &{" "}
                            <Text style={{ fontFamily: "Helvetica-Bold" }}>Nominal Cooling Capacity</Text>{" "}
                            <Text style={styles.smallLabel}>(6.3.1.)</Text>
                        </Text>
                    </View>
                    <SplitCell>
                        <View
                            style={[
                                styles.fullFlex,
                                styles.borderRight,
                                { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
                            ]}
                        >
                            <Text style={styles.rxrValueText}>{parseFloat(heatLossValue).toFixed(0)}</Text>
                            <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 6 }}>{unitLabel}</Text>
                        </View>
                        <View
                            style={[
                                styles.fullFlex,
                                { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
                            ]}
                        >
                            <Text style={styles.rxrValueText}>{parseFloat(latHeatGainValue).toFixed(0)}</Text>
                            <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 6 }}>{unitLabel}</Text>
                        </View>
                    </SplitCell>
                </Row>

                {/* Reference + issued date + page number */}
                <Row>
                    <View style={{ width: 26 }} />
                    <View style={[styles.fullFlex, styles.borderRight, { justifyContent: "center", minHeight: 16 }]}>
                        <Text style={[styles.smallLabel, { textAlign: "right" }]}>
                            {t(
                                "f280pdf.heatingAndCoolingLimits",
                                "See page 1 for heating & Cooling System Capacity Limits"
                            )}
                        </Text>
                    </View>
                    <SplitCell>
                        <Field
                            label={t("f280pdf.issued", "Issued: [!!]")}
                            value={moment(new Date()).format("YYYY-MM-DD")}
                            num="76"
                            labelStyle={{ width: 40 }}
                            containerStyle={styles.borderRight}
                        />
                        <View style={[styles.fieldCell, { alignItems: "center" }]}>
                            <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 7 }}>
                                {t("f280pdf.page", `Page: 2 of 2 [!!]`, { current: 3, total: 3 })}
                            </Text>
                        </View>
                    </SplitCell>
                </Row>

                {/* Footer */}
                <Footer
                    footnote={t("f280pdf.footnote", `Version ${APP_VERSION}. Contact: info@voltaresearch.org [!!]`, {
                        version: APP_VERSION,
                    })}
                    num="63"
                />
            </View>
        </Page>
    );
};

export default RoomByRoomPDFPage;
